/**
 * The permission catalogue and the system roles (slice-00 §8, spec §10.4).
 *
 * Permissions are `resource:action` strings. **Roles are bundles of
 * permissions; code checks permissions, never roles.** The one exception is MFA
 * enforcement, which spec §10.1 defines in terms of roles because no permission
 * expresses it.
 *
 * The whole catalogue is seeded now, including permissions for resources that
 * do not exist yet (`evv:*`, `payroll:*`, `invoice:*`). That is deliberate:
 * later slices then add endpoints and nothing else. The alternative — growing
 * the catalogue slice by slice — means every future slice also touches role
 * seeding, and role seeding is where a separation of duties gets quietly
 * widened by someone who is really trying to ship a feature.
 *
 * Lives in contracts rather than in the API because apps/web filters its
 * navigation on these exact strings (§12). One list, one source of truth.
 */
/**
 * §10.4, as amended. Order preserved so a diff against the spec is readable.
 *
 * Note `auth:*` here is **payer authorizations** (§12), not authentication.
 * The collision is the specification's; it is not renamed, because renaming a
 * catalogue entry to suit one reader is how a catalogue stops matching its
 * spec.
 *
 * ## Amendment C1 — payer and service-code permissions
 *
 * §10.4 has no permission for payers or service codes. When those endpoints
 * landed (slice-01 §6) they borrowed `admin:settings` for writes, and the
 * consequence was wrong in a way that shows up on a Tuesday: **a BILLER could
 * not add a payer or a service code.** A new client arrives with an MCO nobody
 * has billed before, and the person whose job that is has to find an
 * administrator.
 *
 * Added:
 *
 *   payer:read           payer:write
 *   service_code:read    service_code:write
 *                        rate:write
 *
 * Granted so that **BILLER, ADMIN and OWNER hold the writes** and **SCHEDULER
 * and CARE_MANAGER hold the reads**. A scheduler needs to see a service code to
 * understand what a visit is, and has no business setting a price; a care
 * manager reads them for the same reason. ADMIN and OWNER pick the writes up
 * automatically — OWNER is the whole catalogue and ADMIN is the catalogue minus
 * two named denials — so neither needed an edit, and neither denial moved.
 *
 * **This amendment adds entries. It relaxes no exclusion.** Every entry in
 * `DELIBERATE_DENIALS` is unchanged, and `permissions.spec.ts` still asserts
 * each one individually.
 *
 * ## Amendment D2 — `credential:submit_own`
 *
 * §17.2: *"Give the caregiver a direct in-app upload path from the
 * notification — the friction of 'email a photo to the office' is why
 * credentials lapse."* §10.4 has no permission a caregiver could hold for it:
 * `credential:update` is HR's verb over **any** caregiver's record.
 *
 * The note above the CAREGIVER grants says "own" there is row-level, left to
 * §7.3's restrictive policy. That policy is not written yet (scheduling slice),
 * so it cannot carry this one. Instead the endpoint that checks this
 * permission, `POST /v1/field/credentials`, takes **no caregiver id at all** —
 * it records against the caregiver named in the caller's own token — so "own"
 * is a property of the route rather than of a policy that does not exist.
 * Granted to CAREGIVER. OWNER and ADMIN hold it too, because they hold the
 * catalogue, and for them it is inert: the one route that checks it admits a
 * caregiver-audience token and nothing else, and a staff token never carries a
 * caregiver id. It records a PENDING credential — evidence, which somebody
 * holding `credential:verify` still has to look at.
 *
 * **This amendment adds one entry. It relaxes no exclusion.**
 *
 * ## Amendment S7 — `shift:claim_own`
 *
 * §14.5: "First claim wins (row lock); others get 'already filled'." The claim
 * is the caregiver's act, from the offer, and §10.4 has no verb for it:
 * `schedule:update` is a coordinator's power over **any** visit. Like D2, the
 * routes that check it take no caregiver id — `POST /v1/field/offers/:id/claim`
 * and `/decline` act for the caregiver in the caller's own token, on an offer
 * the caregiver-scope policy already limits to theirs. Granted to CAREGIVER;
 * inert for OWNER and ADMIN, whose staff tokens carry no caregiver id and are
 * refused by the field audience before the guard runs. A claim still passes
 * every caregiver rule of the conflict engine before it lands.
 *
 * **This amendment adds one entry. It relaxes no exclusion.**
 *
 * ## Amendment C2 — `rate:update` is retired in favour of `rate:write`
 *
 * §10.4 lists `rate:update`. Rates are insert-only: §6.3.3 says never update a
 * rate row, insert a new one and end-date the old, and the API enforces that by
 * **not having** a PATCH route at all rather than by guarding one.
 *
 * So `rate:update` named an operation that cannot happen. That is not merely
 * untidy — a permission for an operation is a standing invitation to build the
 * operation. The next person to need a price correction finds a permission that
 * says they may update a rate, and the shortest path from there is a PATCH
 * handler, which undoes the decision to enforce the rule by absence.
 *
 * Anyone who held `rate:update` holds `rate:write`. Nothing else changes.
 *
 * This is the one existing entry either amendment alters, and it is a rename
 * rather than a removal of authority.
 */
export const PERMISSION_CATALOGUE = [
    'client:read',
    'client:read_phi',
    'client:create',
    'client:update',
    'client:discharge',
    'careplan:read',
    'careplan:create',
    'careplan:approve',
    'assessment:perform',
    'schedule:read',
    'schedule:create',
    'schedule:update',
    'schedule:delete',
    'schedule:override_conflict',
    'schedule:override_credential',
    'schedule:override_auth',
    'visit:read',
    'visit:document',
    'visit:edit_times',
    'visit:resolve_exception',
    'visit:approve',
    'evv:read',
    'evv:correct',
    'evv:submit',
    'evv:resubmit',
    'caregiver:read',
    'caregiver:read_pay',
    'caregiver:create',
    'caregiver:update',
    'caregiver:terminate',
    'credential:read',
    'credential:update',
    'credential:verify',
    // Amendment D2. See the note above PERMISSION_CATALOGUE.
    'credential:submit_own',
    // Amendment S7. See the note above PERMISSION_CATALOGUE.
    'shift:claim_own',
    'auth:read',
    'auth:create',
    'auth:update',
    // Amendment C1. See the note above PERMISSION_CATALOGUE.
    'payer:read',
    'payer:write',
    'service_code:read',
    'service_code:write',
    'rate:read',
    'rate:write',
    'billing:read',
    'billing:generate_charges',
    'billing:submit_claim',
    'billing:void_claim',
    'billing:post_payment',
    'billing:write_off',
    'billing:override_scrub',
    'invoice:read',
    'invoice:send',
    'invoice:void',
    'payroll:read',
    'payroll:approve',
    'payroll:export',
    'report:read',
    'report:read_financial',
    'report:export',
    'message:send',
    'message:broadcast',
    'admin:users',
    'admin:roles',
    'admin:settings',
    'admin:integrations',
    'admin:audit_log',
    'portal:view_own_client',
];
/**
 * Two permissions §10.4 does not contain, added with their reasons.
 *
 * `session:self` — every §7 endpoint needs either `@RequirePermission` or
 * `@Public` (§8), and `/auth/me`, `/auth/logout` and the MFA endpoints are
 * neither privileged nor public. The catalogue has nothing for "act on your own
 * session", so this exists and is granted to all eight system roles. It is not
 * an authorization control; it is what keeps the decorator rule binary, which
 * is what makes the CI gate able to be binary.
 *
 * `admin:break_glass` — §8's support elevation. Deliberately held by **no
 * seeded agency role**: it belongs to IMO Tech support staff, and an agency
 * role that could grant it would be an agency role that could manufacture
 * support access to its own tenant. Asserted by test.
 */
export const PLATFORM_PERMISSIONS = ['session:self', 'admin:break_glass'];
export const ALL_PERMISSIONS = [...PERMISSION_CATALOGUE, ...PLATFORM_PERMISSIONS];
/**
 * The eight roles seeded per agency (§8).
 *
 * PAYER_READONLY is **not** here — amendment A6. Its audience (PAYER) and its
 * endpoint namespace do not exist until Phase 4, so seeding it would create a
 * role that cannot authenticate.
 */
export const SYSTEM_ROLE_KEYS = [
    'OWNER',
    'ADMIN',
    'SCHEDULER',
    'CARE_MANAGER',
    'BILLER',
    'HR',
    'CAREGIVER',
    'FAMILY',
];
/**
 * The denials §10.4 calls out explicitly.
 *
 * These are separations of duty, not gaps in the mapping, and each one has a
 * reason someone will eventually want to overrule:
 *
 *   ADMIN → billing:write_off   an administrator who can forgive debt can
 *                               forgive their own. Writing off is the biller's,
 *                               and the biller cannot create users.
 *   ADMIN → admin:roles         self-escalation. An admin who can edit roles can
 *                               grant themselves everything above, including
 *                               the write-off they were just denied.
 *   SCHEDULER → caregiver:read_pay
 *                               schedulers see the whole roster all day. Pay is
 *                               the one field that makes that roster sensitive
 *                               to the caregivers on it.
 *   BILLER → visit:document     billing must never be able to author the
 *                               clinical record it bills from. This is the
 *                               single most fraud-relevant line in the file.
 *   BILLER → careplan:create    same reason, one step earlier in the chain.
 *   HR → client:read_phi        HR manages caregivers. Client PHI is not
 *                               theirs, and "beyond assignment lists" is the
 *                               boundary.
 *
 * `permissions.spec.ts` asserts every entry individually, so a role edit that
 * quietly reinstates one fails the build with the reason attached.
 */
export const DELIBERATE_DENIALS = [
    {
        role: 'ADMIN',
        permission: 'billing:write_off',
        because: 'An administrator who can forgive debt can forgive their own.',
    },
    {
        role: 'ADMIN',
        permission: 'admin:roles',
        because: 'Self-escalation: editing roles grants everything else back.',
    },
    {
        role: 'SCHEDULER',
        permission: 'caregiver:read_pay',
        because: 'Schedulers see the whole roster; pay rates are not theirs to see.',
    },
    {
        role: 'BILLER',
        permission: 'visit:document',
        because: 'Billing must never author the clinical record it bills from.',
    },
    {
        role: 'BILLER',
        permission: 'careplan:create',
        because: 'Same separation, one step earlier: no authoring clinical content.',
    },
    {
        role: 'HR',
        permission: 'client:read_phi',
        because: 'HR manages caregivers, not client PHI.',
    },
];
const CATALOGUE_MINUS = (...denied) => PERMISSION_CATALOGUE.filter((permission) => !denied.includes(permission));
/**
 * Default role → permission mapping (§10.4).
 *
 * Every role additionally holds `session:self`; it is added by
 * `permissionsForRole` rather than repeated eight times.
 */
const ROLE_GRANTS = {
    // "All" — the whole catalogue, and nothing from PLATFORM_PERMISSIONS beyond
    // session:self. Even the owner of an agency is not IMO Tech support.
    OWNER: [...PERMISSION_CATALOGUE],
    ADMIN: CATALOGUE_MINUS('billing:write_off', 'admin:roles'),
    SCHEDULER: [
        'schedule:read',
        'schedule:create',
        'schedule:update',
        'schedule:delete',
        // §10.4 grants `schedule:*`, which includes the three overrides. They are
        // the sharpest permissions a scheduler holds — overriding a credential
        // block puts an uncertified caregiver in a client's home — and they are
        // granted because the spec grants them. Every use writes a
        // schedule_override row with a mandatory reason (§4), which is the control
        // that makes them survivable.
        'schedule:override_conflict',
        'schedule:override_credential',
        'schedule:override_auth',
        'visit:read',
        'visit:edit_times',
        'visit:resolve_exception',
        'client:read',
        'client:read_phi',
        'caregiver:read',
        'message:send',
        // C1. A scheduler reads a service code to understand what a visit is, and
        // reads a rate because a shift's cost is part of deciding whether to offer
        // it. Neither write: pricing is not a scheduling decision.
        'payer:read',
        'service_code:read',
        'rate:read',
    ],
    CARE_MANAGER: [
        'careplan:read',
        'careplan:create',
        'careplan:approve',
        'assessment:perform',
        'client:read',
        'client:read_phi',
        'client:create',
        'client:update',
        'client:discharge',
        'visit:read',
        'visit:approve',
        // C1. Reads for the same reason as the scheduler — a care manager builds
        // the plan a service code describes — and no writes.
        'payer:read',
        'service_code:read',
        'rate:read',
    ],
    BILLER: [
        'billing:read',
        'billing:generate_charges',
        'billing:submit_claim',
        'billing:void_claim',
        'billing:post_payment',
        // The biller holds the write-off the admin does not. That asymmetry is the
        // separation working: one role can forgive debt, a different role can
        // create users, and neither can do both.
        'billing:write_off',
        'billing:override_scrub',
        'invoice:read',
        'invoice:send',
        'invoice:void',
        'auth:read',
        'auth:create',
        'auth:update',
        // C1 and C2. This is the amendment's whole point: setting up a payer, its
        // service codes and its rates is the biller's daily work, and borrowing
        // `admin:settings` for it meant a new MCO needed an administrator.
        'payer:read',
        'payer:write',
        'service_code:read',
        'service_code:write',
        'rate:read',
        'rate:write',
        'report:read_financial',
        'visit:read',
    ],
    HR: [
        'caregiver:read',
        'caregiver:read_pay',
        'caregiver:create',
        'caregiver:update',
        'caregiver:terminate',
        'credential:read',
        'credential:update',
        'credential:verify',
    ],
    // "own" everywhere below is row-level, not permission-level: §7.3's
    // RESTRICTIVE caregiver policy scopes these to the caregiver's own rows in
    // the scheduling slice. The permission grants the verb; the policy grants the
    // rows.
    CAREGIVER: [
        'visit:read',
        'visit:document',
        'schedule:read',
        'message:send',
        // D2: their own renewal, from the notification. Own by route, not by row.
        'credential:submit_own',
        // S7: claiming an offered shift. Own by route and by the offer's row policy.
        'shift:claim_own',
    ],
    FAMILY: ['portal:view_own_client'],
};
/** The permissions a system role holds, sorted and deduplicated. */
export function permissionsForRole(role) {
    return [...new Set([...ROLE_GRANTS[role], 'session:self'])].sort();
}
const ROLE_NAMES = {
    OWNER: 'Owner',
    ADMIN: 'Administrator',
    SCHEDULER: 'Scheduler',
    CARE_MANAGER: 'Care Manager',
    BILLER: 'Biller',
    HR: 'HR',
    CAREGIVER: 'Caregiver',
    FAMILY: 'Family',
};
/** Everything the seeder needs, in a stable order. */
export function systemRoleDefinitions() {
    return SYSTEM_ROLE_KEYS.map((key) => ({
        key,
        name: ROLE_NAMES[key],
        permissions: permissionsForRole(key),
    }));
}
//# sourceMappingURL=permissions.js.map